import React, { useEffect, useRef, useState, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import IconProvider from './IconProvider';
import { useZoom } from '@/providers/ZoomProvider';

export interface ContextMenuItem {
  id: string;
  label: string;
  icon?: string;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
  separator?: boolean;
}

interface ContextMenuProps {
  isOpen: boolean;
  position: { x: number; y: number };
  items: ContextMenuItem[];
  onClose: () => void;
}

export default function ContextMenu({ isOpen, position, items, onClose }: ContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [adjustedPosition, setAdjustedPosition] = useState(position);
  const { zoom } = useZoom();

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Adjust position to keep menu within viewport
  // Using useLayoutEffect to calculate before paint for faster positioning
  useLayoutEffect(() => {
    if (!isOpen || !menuRef.current) return;

    const menu = menuRef.current;
    const rect = menu.getBoundingClientRect();
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    let { x, y } = position;

    // getBoundingClientRect() already returns dimensions including CSS transforms (scale)
    // so we use rect.width and rect.height directly - they represent the actual visual size
    const menuWidth = rect.width;
    const menuHeight = rect.height;

    const margin = 8; // Minimum distance from viewport edges

    // Adjust horizontal position
    if (x + menuWidth > viewport.width - margin) {
      // Menu would overflow right edge - position it to the left of the click point
      x = Math.max(margin, position.x - menuWidth);
      
      // If still overflowing, align to right edge with margin
      if (x + menuWidth > viewport.width - margin) {
        x = viewport.width - menuWidth - margin;
      }
    }
    if (x < margin) {
      x = margin;
    }

    // Adjust vertical position
    if (y + menuHeight > viewport.height - margin) {
      // Menu would overflow bottom edge - position it above the click point
      y = Math.max(margin, position.y - menuHeight);
      
      // If still overflowing, align to bottom edge with margin
      if (y + menuHeight > viewport.height - margin) {
        y = viewport.height - menuHeight - margin;
      }
    }
    if (y < margin) {
      y = margin;
    }

    setAdjustedPosition({ x, y });
  }, [isOpen, position, zoom]);

  if (!isOpen) return null;

  const handleItemClick = (item: ContextMenuItem) => {
    if (!item.disabled) {
      item.onClick();
      onClose();
    }
  };

  return createPortal(
    <div
      ref={menuRef}
      className="fixed z-50 min-w-20 bg-popover border border-border rounded-md shadow-lg py-1"
      style={{
        left: adjustedPosition.x,
        top: adjustedPosition.y,
        // Apply zoom transform to match the zoomed content scale
        transform: `scale(${zoom})`,
        transformOrigin: 'top left',
      }}
    >
      {items.map((item, index) => (
        <React.Fragment key={item.id}>
          {item.separator && index > 0 && (
            <div className="h-px bg-border my-1" />
          )}
          <button
            onClick={() => handleItemClick(item)}
            disabled={item.disabled}
            className={`
              w-full px-3 py-2 text-left text-xs flex items-center gap-3 transition-colors
              ${item.disabled 
                ? 'text-muted-foreground cursor-not-allowed' 
                : item.destructive
                  ? 'text-destructive hover:bg-destructive/10 hover:text-destructive'
                  : 'text-popover-foreground hover:bg-accent hover:text-accent-foreground'
              }
            `}
          >
            {item.icon && (
              <IconProvider 
                iconstring={item.icon} 
                size={10} 
                className={item.destructive ? 'text-destructive' : 'text-muted-foreground'} 
              />
            )}
            <span>{item.label}</span>
          </button>
        </React.Fragment>
      ))}
    </div>,
    document.body
  );
}
